import { createContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { requestPasswordRecovery } from '../utils/recoverySession';
import type { User } from '@supabase/supabase-js';
import type { Role } from '../types/auth';
import { useProfileStore } from '../stores/profileStore';
import { useNavigate } from 'react-router-dom';
import type { AuthContextType } from './auth';
import { useMFAStore } from '../stores/mfaStore';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { profile, role, permissions, fetchProfile, setProfile, setRole, setPermissions } =
    useProfileStore();

  const { resetMfaStatus } = useMFAStore();

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled) return;
        setUser(session?.user ?? null);
        // Await profile load before clearing auth loading. Deferring fetchProfile (e.g. setTimeout)
        // previously cleared `loading` first, leaving a frame where user was set but profile
        // fetch had not yet set `loading: true`, so PrivateRoute treated null profile as "needs signup".
        if (session?.user) {
          await fetchProfile(session.user.id);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, [fetchProfile]);

  useEffect(() => {
    // Listen for changes on auth state (sign in, sign out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event == 'PASSWORD_RECOVERY') {
        navigate('/reset-password');
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        // Avoid refetch on TOKEN_REFRESHED (common when returning to the tab): same user/session,
        // and refetching would set profile `loading` and blank routes that gate on it.
        if (event !== 'TOKEN_REFRESHED') {
          // Call immediately so profile store sets `loading` before a paint; setTimeout(0) caused
          // a race where routes saw user + null profile + profile loading still false.
          void fetchProfile(session.user.id);
        }
      } else {
        setProfile(null);
        setRole(null);
        setPermissions([]);
      }
    });

    return () => subscription?.unsubscribe?.();
  }, [fetchProfile, navigate, setPermissions, setProfile, setRole]);

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw error;
    const userId = data.user?.id;

    if (userId) {
      await fetchProfile(userId);
    }
  };

  const signUp = async (
    firstName: string,
    email: string,
    password: string,
    notificationAccepted: boolean = true,
    newsletterAccepted: boolean = false,
    emailRedirectTo?: string
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo,
        data: {
          firstName: firstName.trim(),
          notificationAccepted,
          newsletterAccepted,
        },
      },
    });
    if (error) throw error;

    const user = data.user;
    if (!user) throw new Error('User not returned from signUp');
    const isConfirmed = user.email_confirmed_at !== null && user.email_confirmed_at !== undefined;
    setUser(user);
    return { isConfirmed, email: user.email };
  };

  const signOut = async () => {
    resetMfaStatus();
    setUser(null);
    setProfile(null);
    setRole(null);
    setPermissions([]);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    await requestPasswordRecovery(email);
  };

  const updatePassword = async (currentPassword: string, newPassword: string, email: string) => {
    // Re-authentification avec l'email actuel + mot de passe fourni
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });

    if (signInError) {
      console.error('Re-authentication failed:', signInError);
      return signInError.message;
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const updateResetPassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  };

  const hasProfile = Boolean(profile?.last_name);

  const resendConfirmationEmail = async (email?: string) => {
    if (!email && !user?.email) throw new Error('No email address found');
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email || user?.email || '',
    });
    if (error) throw error;
  };

  const hasPermission = (permission: string) => permissions.includes(permission);
  const hasRole = (role: Role) => profile?.role === role;

  const updateEmail = async (email: string) => {
    const { error } = await supabase.auth.updateUser(
      { email: email.trim() },
      {
        emailRedirectTo: `${window.location.origin}/login`,
      }
    );
    if (error) throw error;
  };

  const doesEmailExist = async (p_email: string) => {
    const { data, error } = await supabase.rpc('email_exists', { p_email: p_email.trim() });

    if (error) {
      console.error('Error checking email existence:', error);
      throw error;
    }

    return data === true;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        permissions,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        updateResetPassword,
        hasProfile,
        resendConfirmationEmail,
        hasPermission,
        hasRole,
        updateEmail,
        doesEmailExist,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
